import {
  useQueryClient,
  useMutation,
  useQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { useParams } from "react-router";
import { QueryKey } from "./types";
import {
  confirmCostEstimates,
  createOperation,
  deleteRfq,
  fetchQuoteMessage,
  fetchRfq,
  updateQuoteMessage,
  deleteOperation,
  moveOperation,
  fetchSuggestOperations,
  addOrganization,
  editOrganization,
  deleteOrganization,
  addNote,
} from "./apis";
import { Operation } from "@/pages/part/operationList/lib/types";

import useGetCurrentQuoteRevision from "../SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import $api from "./openapi";

export function useFetchRfqs(
  statusFilter: string[],
  customerFilter: string[],
  contactFilter: string[],
  search: string,
) {
  const { teamSlug } = useParams();
  const { data, isLoading, isFetching } = useQuery({
    enabled: !!teamSlug,
    refetchInterval: 30 * 1000,
    placeholderData: keepPreviousData,
    ...$api.queryOptions("get", "/api/teams/{team_slug}/rfqs", {
      params: {
        path: {
          team_slug: teamSlug!,
        },
        query: {
          status_in: statusFilter,
          organization_in: customerFilter,
          contact_in: contactFilter,
          search: search,
        },
      },
    }),
  });

  return { data: data?.items ?? [], isLoading, isFetching };
}

export function useFetchRfq() {
  const { rfqId, teamSlug } = useParams();
  const { data } = useQuery({
    queryKey: [QueryKey.rfq, teamSlug, rfqId],
    queryFn: async () => fetchRfq(teamSlug!, rfqId!),
    enabled: !!teamSlug && !!rfqId,
  });
  return data;
}

export function useFetchRfqNew() {
  const { rfqId, teamSlug } = useParams();
  return $api.useQuery("get", "/api/teams/{team_slug}/rfqs/{id}/full", {
    params: {
      path: {
        team_slug: teamSlug!,
        id: rfqId!,
      },
    },
  });
}

export function useFetchPart() {
  const { partId, teamSlug } = useParams();
  return $api.useQuery(
    "get",
    "/api/teams/{team_slug}/requested_parts/{id}",
    {
      params: {
        path: {
          team_slug: teamSlug!,
          id: partId!,
        },
      },
    },
    {
      enabled: !!teamSlug && !!partId,
    },
  );
}

export function useFetchSuggestOperations() {
  const { partId, teamSlug } = useParams();
  const { data } = useQuery({
    queryKey: [QueryKey.suggestedOperations, teamSlug, partId],
    queryFn: async () => fetchSuggestOperations(teamSlug!, partId!),
    enabled: !!teamSlug && !!partId,
  });
  return data;
}

export function useDeleteOperation() {
  const queryClient = useQueryClient();
  const { teamSlug } = useParams();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({ operationId }: { operationId: string }) =>
      deleteOperation(teamSlug!, operationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/requested_parts/{id}"],
      });
      queryClient.invalidateQueries({
        // part tree relies on part operations now with `getPartBadge`
        queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
      });
    },
  });
  return { deleteOperation: mutate, isPending };
}

export function useMoveOperation({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: (data: Operation) => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { teamSlug, partId } = useParams();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({ id, ordinal }: { id: string; ordinal: string }) =>
      moveOperation(teamSlug!, id, ordinal),
    onSuccess: (operation) => {
      queryClient.invalidateQueries({
        queryKey: [
          "get",
          "/api/teams/{team_slug}/requested_parts/{id}",
          {
            params: {
              path: {
                team_slug: teamSlug!,
                id: partId!,
              },
            },
          },
        ],
      });
      onSuccess(operation);
    },
    onError,
  });
  return { moveOperation: mutate, isPending };
}

export function useCreateOperation() {
  const queryClient = useQueryClient();
  const { teamSlug, partId } = useParams();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      name,
      type,
      draft,
      ordinal,
    }: {
      name: string;
      type: string;
      draft: object;
      ordinal: string;
    }) => createOperation(teamSlug!, partId!, name, type, draft, ordinal),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/requested_parts/{id}"],
      });
      queryClient.invalidateQueries({
        // part tree relies on part operations now with `getPartBadge`
        queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/full"],
      });
    },
  });
  return { createOperation: mutate, isPending };
}

export function useConfirmCostEstimates() {
  const queryClient = useQueryClient();
  const { teamSlug, rfqId } = useParams();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({ quoteRevisionId }: { quoteRevisionId: string }) =>
      confirmCostEstimates(teamSlug!, quoteRevisionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.rfq, teamSlug, rfqId],
      });
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/rfqs"],
      });
    },
  });
  return { confirmCostEstimates: mutate, isPending };
}

export function useFetchQuoteMessage() {
  const { teamSlug } = useParams();
  const quoteRevision = useGetCurrentQuoteRevision();
  const quoteRevisionId = quoteRevision?.id;
  const { data } = useQuery({
    queryKey: [QueryKey.quoteMessage, teamSlug, quoteRevisionId],
    queryFn: async () => fetchQuoteMessage(teamSlug!, quoteRevisionId!),
    enabled: !!teamSlug && !!quoteRevisionId,
  });
  return data;
}

export function useUpdateQuoteMessage({
  onSuccess = () => {},
  onError = () => {},
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const queryClient = useQueryClient();
  const { teamSlug } = useParams();
  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      quoteRevisionId,
      message,
    }: {
      quoteRevisionId: string;
      message: string;
    }) => updateQuoteMessage(teamSlug!, quoteRevisionId, message),
    onSuccess: (_, { quoteRevisionId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.quoteMessage, teamSlug, quoteRevisionId],
      });
      onSuccess();
    },
    onError,
  });
  return { updateQuoteMessage: mutate, isPending };
}

export function useDeleteRfq() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (id: string) => deleteRfq(teamSlug!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/rfqs"],
      });
    },
  });
  return mutate;
}

export function useFetchContacts() {
  const { teamSlug } = useParams();
  const { data } = $api.useQuery("get", "/api/teams/{team_slug}/contacts", {
    params: {
      path: {
        team_slug: teamSlug!,
      },
    },
  });

  return data?.items ?? [];
}

export function useFetchOrganizations(search: string) {
  const { teamSlug } = useParams();
  const { data, isFetching, isLoading } = useQuery({
    placeholderData: keepPreviousData,
    ...$api.queryOptions("get", "/api/teams/{team_slug}/organizations", {
      params: {
        path: {
          team_slug: teamSlug!,
        },
        query: {
          search,
        },
      },
    }),
  });
  return { data: data?.items ?? [], isFetching, isLoading };
}

export function useAddOrganization() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (data: {
      name: string;
      email_domain: string;
      accounting_system_organization_id: string | null;
    }) =>
      addOrganization(
        teamSlug!,
        data.name,
        data.email_domain,
        data.accounting_system_organization_id,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/organizations"],
      });
    },
  });
  return mutate;
}

export function useEditOrganization() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (data: {
      name: string;
      email_domain: string;
      accounting_system_organization_id: string | null;
      id: string;
    }) =>
      editOrganization(
        teamSlug!,
        data.id,
        data.name,
        data.email_domain,
        data.accounting_system_organization_id,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/organizations"],
      });
    },
  });
  return mutate;
}

export function useDeleteOrganization() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (data: { id: string }) =>
      deleteOrganization(teamSlug!, data.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/organizations"],
      });
    },
  });
  return mutate;
}

export function useAddNote() {
  const { teamSlug } = useParams();
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async (data: { rfqId: string; content: string }) =>
      addNote(teamSlug!, data.rfqId, data.content),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get", "/api/teams/{team_slug}/rfqs/{id}/timeline"],
      });
    },
  });
  return mutate;
}
